import React, { useState, useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Input } from "../../components/ui/input";
import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../../components/ui/form";

interface AmountControllerProps {
  name: string;
  label: string;
  placeholder: string;
  defaultValue?: string | number;
  fixed?: string;
  validation?: {
    required?: string;
  };
  infoMessage?: string;
  onChange?: (value: any) => void;
  readOnly?: boolean;
}

// const AmountController: React.FC<AmountControllerProps> = ({
//   name,
//   label,
//   placeholder,
//   defaultValue,
//   fixed = "2",
//   validation = {},
//   infoMessage,
//   onChange,
// }) => {
//   const { control, setValue } = useFormContext();
//   const [internalValue, setInternalValue] = useState<string>("");
//   const [error, setError] = useState<string | null>(null);

//   useEffect(() => {
//     if (defaultValue !== undefined) {
//       const formattedValue = formatCurrencyValue(defaultValue);
//       setInternalValue(formattedValue);
//       setValue(name, parseValue(formattedValue)); // Set initial form value
//     }
//   }, [defaultValue, setValue, name]);

//   const formatCurrencyValue = (value: number | string): string => {
//     const num = typeof value === "string" ? parseFloat(value) : value;
//     return num.toLocaleString("en-US", {
//       style: "currency",
//       currency: "USD",
//       minimumFractionDigits: parseInt(fixed),
//       maximumFractionDigits: parseInt(fixed),
//     });
//   };

//   const parseValue = (value: string): number => {
//     const cleanedValue = value.replace(/[^0-9.-]+/g, "");
//     return parseFloat(cleanedValue) || 0;
//   };

//   const handleValidation = (value: string): boolean => {
//     const parsedValue = parseValue(value);
//     // console.log(parsedValue, 999);
//     if (isNaN(parsedValue)) {
//       setError("Amount must be a number.");
//       return false;
//     }
//     if (parsedValue < 0) {
//       setError("Amount must be non-negative.");
//       return false;
//     }
//     setError(null);
//     return true;
//   };

//   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const rawValue = e.target.value;
//     setInternalValue(rawValue);
//     handleValidation(rawValue);
//   };

//   const handleInputBlur = () => {
//     const parsedValue = parseValue(internalValue);
//     const formattedValue = formatCurrencyValue(parsedValue);
//     setInternalValue(formattedValue);
//     if (handleValidation(internalValue) && onChange) {
//       onChange(parsedValue);
//     }
//     setValue(name, parsedValue); // Update form value
//   };

//   return (
//     <Controller
//       name={name}
//       control={control}
//       rules={{
//         ...validation,
//         validate: (value) => handleValidation(formatCurrencyValue(value)),
//       }}
//       render={({
//         field: { onChange: fieldOnChange, value, ...fieldProps },
//         fieldState: { error: fieldError },
//       }) => (
//         <FormItem className="mb-6">
//           <FormLabel htmlFor={name}>{label}</FormLabel>
//           <FormControl>
//             <div className="flex">
//               <span className="flex justify-center items-center w-10 h-10 bg-gray-300 text-white rounded mr-1">
//                 $
//               </span>
//               <Input
//                 {...fieldProps}
//                 id={name}
//                 type="text"
//                 placeholder={placeholder}
//                 value={
//                   internalValue ||
//                   (defaultValue !== undefined
//                     ? formatCurrencyValue(defaultValue)
//                     : "$0.00")
//                 }
//                 onChange={(e) => {
//                   handleInputChange(e);
//                   const parsedValue = parseValue(e.target.value);
//                   fieldOnChange(parsedValue);
//                 }}
//                 onBlur={handleInputBlur}
//               />
//             </div>
//           </FormControl>
//           <FormMessage
//             className={
//               fieldError || error
//                 ? "text-red-500"
//                 : "text-gray-500 text-xs font-normal"
//             }
//           >
//             {fieldError ? fieldError.message : error ? error : infoMessage}
//           </FormMessage>
//         </FormItem>
//       )}
//     />
//   );
// };

const AmountController: React.FC<AmountControllerProps> = ({
  name,
  label,
  placeholder,
  defaultValue,
  fixed = "2",
  validation = {},
  infoMessage,
  onChange,
  readOnly = false,
}) => {
  const { control, setValue, setError, clearErrors } = useFormContext();
  const [internalValue, setInternalValue] = useState<string>("$0.00");

  useEffect(() => {
    if (defaultValue !== undefined) {
      const initialValue = parseValue(defaultValue.toString());
      const formattedValue = formatCurrencyValue(initialValue);
      setInternalValue(formattedValue);
      setValue(name, initialValue); // Set the value in the form context
    } else {
      setInternalValue("$0.00"); // Default display value for an amount input
    }
  }, [defaultValue, setValue, name]);

  const formatCurrencyValue = (value: number | string): string => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: parseInt(fixed),
      maximumFractionDigits: parseInt(fixed),
    });
  };

  const parseValue = (value: string): number => {
    const cleanedValue = value.replace(/[^0-9.-]+/g, "");
    return parseFloat(cleanedValue) || 0;
  };

  const handleValidation = (value: string): boolean => {
    if (readOnly) return true; // Skip validation for read-only fields

    const parsedValue = parseValue(value);
    if (isNaN(parsedValue)) {
      setError(name, {
        type: "manual",
        message: "Amount must be a valid number.",
      });
      return false;
    }
    if (parsedValue < 0) {
      setError(name, {
        type: "manual",
        message: "Amount must be non-negative.",
      });
      return false;
    }
    clearErrors(name);
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return; // Don't update state if read-only

    const rawValue = e.target.value;
    setInternalValue(rawValue); // Update with raw input value during typing
    handleValidation(rawValue);
  };

  const handleInputBlur = () => {
    if (readOnly) return; // Don't update state if read-only

    const parsedValue = parseValue(internalValue);
    const formattedValue = formatCurrencyValue(parsedValue);
    setInternalValue(formattedValue); // Format the value on blur
    if (handleValidation(internalValue) && onChange) {
      onChange(parsedValue);
    }
    setValue(name, parsedValue);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={validation}
      render={({
        field: { onChange: fieldOnChange, value: fieldValue, ...fieldProps },
        fieldState: { error: fieldError },
      }) => {
        // CRITICAL: Use field.value as source of truth for reset() to work
        const displayValue = fieldValue ?? internalValue;
        
        return (
          <FormItem className="mb-6">
            <FormLabel htmlFor={name}>{label}</FormLabel>
            <FormControl>
              <div className="flex">
                <span className="flex justify-center items-center w-10 h-10 bg-gray-300 text-white rounded mr-1">
                  $
                </span>
                <Input
                  {...fieldProps}
                  id={name}
                  type="text"
                  placeholder={placeholder}
                  value={displayValue} // Display formatted value
                onChange={(e) => {
                  if (readOnly) return; // Don't process onChange if read-only

                  handleInputChange(e);
                  const parsedValue = parseValue(e.target.value);
                  fieldOnChange(parsedValue); // Update form value
                }}
                onBlur={handleInputBlur}
                readOnly={readOnly}
                className={readOnly ? "bg-gray-100 cursor-not-allowed" : ""}
              />
            </div>
          </FormControl>
            <FormMessage
              className={
                fieldError ? "text-red-500" : "text-gray-500 text-xs font-normal"
              }
            >
              {fieldError ? fieldError.message : infoMessage}
            </FormMessage>
          </FormItem>
        );
      }}
    />
  );
};

export default AmountController;
